import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { type ReactNode, createContext, useContext } from 'react';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box } from '@mui/material';

type SortableId = string | number;
type SortableLayout = 'list' | 'grid';

type HandleContextValue = {
  attributes: ReturnType<typeof useSortable>['attributes'];
  listeners: ReturnType<typeof useSortable>['listeners'];
};

const LayoutContext = createContext<SortableLayout>('list');
const HandleContext = createContext<HandleContextValue | null>(null);

export interface SortableListProps<T extends { id: SortableId }> {
  items: T[];
  onReorder: (items: T[]) => void;
  layout?: SortableLayout;
  children: ReactNode;
}

export function SortableList<T extends { id: SortableId }>({
  items,
  onReorder,
  layout = 'list',
  children,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <LayoutContext.Provider value={layout}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={
            layout === 'grid'
              ? rectSortingStrategy
              : verticalListSortingStrategy
          }
        >
          {children}
        </SortableContext>
      </DndContext>
    </LayoutContext.Provider>
  );
}

export interface SortableItemProps {
  id: SortableId;
  /** When true, only a nested `DragHandle` starts a drag; otherwise the whole item does. */
  dragHandle?: boolean;
  children: ReactNode;
}

export function SortableItem({
  id,
  dragHandle = false,
  children,
}: SortableItemProps) {
  const layout = useContext(LayoutContext);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const toTransform = layout === 'grid' ? CSS.Translate : CSS.Transform;

  return (
    <HandleContext.Provider
      value={dragHandle ? { attributes, listeners } : null}
    >
      <Box
        ref={setNodeRef}
        style={{
          transform: toTransform.toString(transform),
          transition,
          opacity: isDragging ? 0.5 : 1,
          zIndex: isDragging ? 1 : undefined,
          position: 'relative',
        }}
        {...(dragHandle ? {} : { ...attributes, ...listeners })}
        sx={dragHandle ? undefined : { touchAction: 'none' }}
      >
        {children}
      </Box>
    </HandleContext.Provider>
  );
}

export function DragHandle() {
  const handle = useContext(HandleContext);
  if (!handle) {
    return null;
  }

  return (
    <Box
      {...handle.attributes}
      {...handle.listeners}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      sx={{
        display: 'flex',
        color: 'text.disabled',
        cursor: 'grab',
        touchAction: 'none',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <DragIndicatorIcon fontSize="small" />
    </Box>
  );
}
